import type { RestSchema } from "@mountaya/client";
import { z } from "zod";

import type { components } from "../../schema/api";

/**
 * OrganizationRole is one membership role, and it is the CONTRACT's type rather than a local
 * one.
 *
 * It used to be `z.infer<typeof organizationRoleSchema>`, which made the schema the source of
 * truth for a vocabulary the contract already owns — so a role added to the backend and
 * regenerated into the client left this enum quietly short, and every parse rejected the new
 * role as out of range.
 *
 * `satisfies z.ZodType<OrganizationRole>` below is HALF the guarantee, and only half:
 * measured, a schema producing a NARROWER union still satisfies it, because a narrower output
 * is assignable to a wider one. It catches a member this schema invents that the contract does
 * not have; it does not catch one the contract gained and this schema lacks — which is the
 * direction that actually broke. `roles.test.ts` holds that direction with a type-level
 * equality, because there is no runtime list of the contract's members to compare against.
 */
export type OrganizationRole = RestSchema.OrganizationRole;

/**
 * organizationRoleSchema mirrors the backend organization-membership role enum.
 * The roles form a privilege ladder: owner (full control, exactly one per org) >
 * admin (manage members, keys, settings, content) > contributor (create/edit
 * content) > viewer (read-only). Zod-first at the BOUNDARY: every role-typed field
 * across the api layer parses through it, and `satisfies` holds it to the contract.
 */
export const organizationRoleSchema = z.enum([
  "owner",
  "admin",
  "contributor",
  "viewer",
]) satisfies z.ZodType<OrganizationRole>;

/**
 * OrganizationStatus is an organization's lifecycle status, and it is the console's type: the
 * public organization no longer carries it, the account read (`getOrganizationAccount`) and the
 * staff search do, so it is spelled off the generated document rather than the published client.
 */
export type OrganizationStatus = components["schemas"]["OrganizationStatus"];

/**
 * organizationStatusSchema mirrors the backend OrganizationStatus enum: an
 * organization is "active" (set up + operational) or "canceled" (its
 * subscription ended). Hoisted here so the account read and the staff search share one
 * definition.
 */
export const organizationStatusSchema = z.enum([
  "active",
  "canceled",
]) satisfies z.ZodType<OrganizationStatus>;

/**
 * AssignableOrganizationRole is a role an owner or admin can grant, by invitation or by
 * changing a member's role, and it is the contract's type for the same reason the role
 * above is. It is OrganizationRole without owner: an organization has exactly one owner,
 * and that role is never granted through either operation, only moved by a transfer.
 */
export type AssignableOrganizationRole = RestSchema.AssignableOrganizationRole;

/**
 * ASSIGNABLE_ROLES are every role a membership can be granted, by invitation or by a change of
 * role. owner is excluded: ownership moves only by a transfer. The order is the privilege order,
 * so a role Select renders highest-to-lowest. `satisfies` holds each member to the contract;
 * `roles.test.ts` holds the list to ALL of the contract's members. Who may grant which of them is
 * {@link assignableRolesFor} and {@link invitableRolesFor}.
 */
export const ASSIGNABLE_ROLES = [
  "admin",
  "contributor",
  "viewer",
] as const satisfies readonly AssignableOrganizationRole[];

/**
 * MANAGED_BY_ADMIN are the roles an admin governs: they invite with them, move members between
 * them and remove members holding them. `admin` itself is the owner's alone to grant, revoke or
 * remove, so an admin never governs another admin, themself included.
 */
const MANAGED_BY_ADMIN = [
  "contributor",
  "viewer",
] as const satisfies readonly AssignableOrganizationRole[];

/**
 * NO_ROLES is the answer for an actor who grants nothing: a contributor, a viewer, and anybody
 * acting on the owner, whose role no request changes.
 */
const NO_ROLES: readonly AssignableOrganizationRole[] = [];

/**
 * MemberStanding is what the role rules read of a person in an organization: who they are and the
 * role they hold. A member listing row and the signed-in user's own membership both carry it.
 */
export interface MemberStanding {
  readonly role: OrganizationRole;
  readonly user_id: string;
}

/**
 * isOrganizationAdmin reports whether a role grants administrative access —
 * owner or admin. The privileged tabs (Usage, API keys) and the
 * member/key/profile mutations gate on this.
 */
export const isOrganizationAdmin = (role: OrganizationRole): boolean =>
  role === "owner" || role === "admin";

/**
 * invitableRolesFor returns the roles an actor may invite with, in privilege order: every
 * assignable role for the owner, contributor and viewer for an admin (only the owner invites as
 * `admin`), and none for anybody else, who may not invite at all. It mirrors the rule
 * `postOrganizationInvitation` refuses with a 403, so the form never offers a role the API would
 * refuse.
 */
export const invitableRolesFor = (
  actor: OrganizationRole,
): readonly AssignableOrganizationRole[] => {
  if (actor === "owner") {
    return ASSIGNABLE_ROLES;
  }

  return actor === "admin" ? MANAGED_BY_ADMIN : NO_ROLES;
};

/**
 * assignableRolesFor returns the roles an actor may move a member to, in privilege order, or none
 * when the member's role is not the actor's to change. It mirrors `patchOrganizationMember`:
 *
 * - the owner's role is nobody's to change, the owner's own included (a 409 there): ownership
 *   moves only by a transfer;
 * - the owner moves any other member between admin, contributor and viewer;
 * - an admin moves contributors and viewers between those two, and never touches a member who
 *   holds `admin`, their own role included (a 403 there);
 * - a contributor or a viewer changes nobody's role.
 *
 * The member's current role is among the answer whenever the answer is not empty, so a role
 * Select can show it as selected.
 */
export const assignableRolesFor = (
  actor: MemberStanding,
  target: MemberStanding,
): readonly AssignableOrganizationRole[] => {
  if (target.role === "owner") {
    return NO_ROLES;
  }

  if (actor.role === "owner") {
    return ASSIGNABLE_ROLES;
  }

  if (actor.role === "admin" && target.role !== "admin") {
    return MANAGED_BY_ADMIN;
  }

  return NO_ROLES;
};

/**
 * canRemoveMember reports whether an actor may remove a member, which is leaving when the member
 * is the actor. It mirrors `deleteOrganizationMember`:
 *
 * - the owner can be neither removed nor leave (a 409 there): ownership has to be transferred
 *   first;
 * - every other role may leave;
 * - the owner removes anybody else;
 * - an admin removes contributors and viewers, never another admin;
 * - a contributor or a viewer removes nobody.
 */
export const canRemoveMember = (actor: MemberStanding, target: MemberStanding): boolean => {
  if (target.role === "owner") {
    return false;
  }

  if (actor.user_id === target.user_id) {
    return true;
  }

  if (actor.role === "owner") {
    return true;
  }

  return actor.role === "admin" && target.role !== "admin";
};

/**
 * canTransferOwnership reports whether an actor may make a member the organization's owner: only
 * the owner may, and only to another member, as `postOrganizationTransferOwnership` holds. The
 * outgoing owner becomes an admin.
 */
export const canTransferOwnership = (actor: MemberStanding, target: MemberStanding): boolean =>
  actor.role === "owner" && actor.user_id !== target.user_id;
